type TrackParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: any;
    _fbq?: any;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GOOGLE_ADS_CONVERSION_ID = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID as string | undefined;
const GOOGLE_ADS_CONVERSION_LABEL = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL as string | undefined;

let trackingInitialized = false;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function sanitizeParams(params: TrackParams | undefined): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  if (!params) return out;

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;

    const k = key.toLowerCase();
    const looksSensitive =
      k.includes('phone') ||
      k.includes('email') ||
      k.includes('address') ||
      k.includes('first_name') ||
      k.includes('last_name') ||
      k === 'name' ||
      k.includes('fullname');

    if (looksSensitive) continue;

    // Only allow primitive-ish values.
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
      out[key] = value;
    }
  }

  return out;
}

function resolveGaMeasurementId(): string | undefined {
  // Always allow explicit override via env for non-standard hosts.
  if (!isBrowser()) return GA_MEASUREMENT_ID;

  const host = window.location.hostname.toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1') return 'G-8SFZ0K9CEL';
  if (host.includes('hales')) return 'G-34LH6NK6MQ';
  if (host.includes('abraham')) return 'G-HSTZBCSH1J';
  if (host.includes('climate') || host.includes('my-ccs') || host.includes('myccs')) return 'G-8SFZ0K9CEL';

  return GA_MEASUREMENT_ID;
}

function ensureGtagLoaded(): void {
  if (!isBrowser()) return;

  const measurementId = resolveGaMeasurementId();
  const loaderId = measurementId || GOOGLE_ADS_CONVERSION_ID;
  if (!loaderId) return;

  if (!document.querySelector('script[data-membership-gtag="true"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(loaderId)}`;
    script.setAttribute('data-membership-gtag', 'true');
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];

  // Stub gtag to queue until the external script loads.
  if (!window.gtag) {
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer?.push(args);
    };
  }

  window.gtag('js', new Date());

  if (measurementId) {
    window.gtag('config', measurementId, {
      anonymize_ip: true,
      // SPA pageviews should be tracked explicitly.
      send_page_view: false,
    });
  }

  if (GOOGLE_ADS_CONVERSION_ID) {
    window.gtag('config', GOOGLE_ADS_CONVERSION_ID);
  }
}

function ensureMetaPixelLoaded(): void {
  if (!isBrowser()) return;
  if (!META_PIXEL_ID) return;

  if (window.fbq) return;

  // Standard Meta Pixel snippet (queued stub).
  /* eslint-disable no-param-reassign */
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod(...Array.from(arguments)) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    t.setAttribute('data-membership-fbpixel', 'true');
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable no-param-reassign */

  window.fbq('init', META_PIXEL_ID);
}

export function initMembershipTracking(): void {
  if (!isBrowser()) return;
  if (trackingInitialized) return;
  trackingInitialized = true;

  ensureGtagLoaded();
  ensureMetaPixelLoaded();
}

export function trackEvent(eventName: string, params?: TrackParams): void {
  if (!isBrowser()) return;

  initMembershipTracking();
  const safe = sanitizeParams(params);

  const measurementId = resolveGaMeasurementId();

  if (window.gtag && (measurementId || GOOGLE_ADS_CONVERSION_ID)) {
    window.gtag('event', eventName, safe);
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('trackCustom', eventName, safe);
  }
}

export function trackPageView(params: {
  page_path: string;
  page_title?: string;
  page_location?: string;
  [key: string]: string | number | boolean | null | undefined;
}): void {
  if (!isBrowser()) return;

  initMembershipTracking();

  const safe = sanitizeParams(params);

  const measurementId = resolveGaMeasurementId();

  if (window.gtag && measurementId) {
    window.gtag('event', 'page_view', safe);
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'PageView');
  }

  trackEvent('membership_page_view', safe);
}

export function trackLead(params: TrackParams): void {
  if (!isBrowser()) return;

  initMembershipTracking();

  const safe = sanitizeParams(params);

  const measurementId = resolveGaMeasurementId();

  if (window.gtag && measurementId) {
    window.gtag('event', 'generate_lead', safe);
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'Lead', safe);
  }

  trackEvent('membership_lead', safe);
}

export function trackBeginCheckout(params: TrackParams & { value: number; currency: string }): void {
  if (!isBrowser()) return;

  initMembershipTracking();

  const safe = sanitizeParams(params);

  const measurementId = resolveGaMeasurementId();

  if (window.gtag && measurementId) {
    window.gtag('event', 'begin_checkout', safe);
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'InitiateCheckout', safe);
  }

  trackEvent('membership_checkout_started', safe);
}

export function trackPurchase(
  params: TrackParams & {
    value: number;
    currency: string;
    transaction_id?: string;
  }
): void {
  if (!isBrowser()) return;

  initMembershipTracking();

  const safe = sanitizeParams(params);

  const measurementId = resolveGaMeasurementId();

  if (window.gtag && measurementId) {
    window.gtag('event', 'purchase', safe);
  }

  if (window.gtag && GOOGLE_ADS_CONVERSION_ID && GOOGLE_ADS_CONVERSION_LABEL) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
      value: safe.value,
      currency: safe.currency,
    });
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'Purchase', {
      value: safe.value,
      currency: safe.currency,
    });
  }

  trackEvent('membership_purchase_success', safe);
}
